import { Button, Modal, Form, message } from "antd";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircleFilled,
  CloseOutlined,
  Loading3QuartersOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { handleAssignApprover } from "../../../api/units";

const REACT_APP_BASE_API_URL = process.env.REACT_APP_BASE_API_URL;

export default function AssignApprover({
  isVisible,
  setVisible,
  selected,
}: any) {
  const [keyword, setKeyword] = useState<any>(null);
  const [found, setFound] = useState(false);
  const [isSearching, setSearching] = useState<any>(false);
  const [err, setErr] = useState<any>(null);
  const [errMessage, setErrMessage] = useState<any>("");
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [records, setRecords] = useState<any>(null);
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const { mutate, isPending } = useMutation({
    mutationFn: handleAssignApprover,
    onSuccess: () => {
      message.success("Created Successfully");
      clearForm();
      setVisible(false);
      queryClient.invalidateQueries({ queryKey: ["units"] });
    },
    onError: () => {
      message.error("Something went wrong");
    },
  });

  const onSubmit = async () => {
    const data = { approver: selectedUser?._id };
    await mutate({ data, _id: selected?._id });
  };

  const handleSearchUser = async () => {
    try {
      setSearching(true);

      if (!keyword) {
        setSearching(false);
        return;
      }

      const token = localStorage.getItem("token");

      const response = await fetch(
        `${REACT_APP_BASE_API_URL}/v1/users?page=1&limit=10&name=${keyword}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();
      if (result?.records?.length > 0) {
        setFound(true);
        setRecords(result?.records);
      } else {
        setRecords([]);
        setFound(false);
        setErrMessage("User not found, Please Fill in below");
      }
      setSearching(false);
    } catch (err) {
      console.log(err);
    }
  };

  const handleSelected = (item: any) => {
    setErrMessage("");
    setSelectedUser(item);
    form.setFieldsValue({
      approver: item?.name,
    });
    setRecords([item]);
  };

  const clearForm = async () => {
    form.resetFields();
    setKeyword("");
    setFound(false);
    setRecords([]);
    setErrMessage("");
    setSearching(false);
    setSelectedUser(null);
  };

  const handleClearSearch = () => {
    form.resetFields();
    setKeyword("");
    setFound(false);
    setRecords([]);
    setErrMessage("");
    setSearching(false);
    setSelectedUser(null);
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      handleSearchUser();
    }, 1000);
    return () => clearTimeout(delay);
  }, [keyword]);

  return (
    <Modal
      open={isVisible}
      title="Assign Approver"
      onCancel={() => setVisible(false)}
      footer={[
        <Button key="back" onClick={clearForm}>
          Clear
        </Button>,
        <Button
          key="submit"
          form="assignApprover"
          htmlType="submit"
          type="primary"
          onSubmit={onSubmit}
          loading={isPending}
        >
          Submit
        </Button>,
      ]}
    >
      <div className="">
        <div className="flex border-b-4 pb-2">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search user or staff name"
              className={`border py-1 px-2 rounded-sm flex-1 ${err ? "border-red-500" : "border-gray-400"
                }`}
              value={keyword}
              style={{ width: "100%" }}
              onChange={(e) => {
                setErr(false);
                setKeyword(e.target.value);
              }}
            />
          </div>
          <button
            onClick={() => (found ? handleClearSearch() : handleSearchUser())}
            className={`  text-white font-bold py-1 p-2 ml-2 ${found
              ? "bg-red-500 hover:bg-red-700"
              : "bg-blue-500 hover:bg-blue-700"
              }`}
          >
            {isSearching ? (
              <Loading3QuartersOutlined color="#FFFFFF" />
            ) : (
              <div className="">
                {found ? (
                  <CloseOutlined color="#FFFFFF" />
                ) : (
                  <SearchOutlined color="#FFFFFF" />
                )}
              </div>
            )}
          </button>
        </div>

        <div className="mt-1">
          {records?.length > 0 ? (
            <div>
              {records?.map((item: any, index: number) => (
                <button
                  className={`flex items-center bg-gray-100 p-2 mb-2 border w-full ${item?._id === selectedUser?._id ? "bg-green-50" : ""
                    }`}
                  onClick={() => handleSelected(item)}
                  key={index}
                >
                  <div
                    className={`flex justify-center items-center w-6 h-6 -mt-2 mr-2 border border-gray-400 ${item?._id === selectedUser?._id &&
                      "bg-green-500 text-white"
                      }`}
                  >
                    {item?._id === selectedUser?._id && (
                      <div className="">
                        <CheckCircleFilled />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col justify-center flex-1">
                    <div className="flex font-bold">{item?.name}</div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center">
              <div className="text-gray-500 text-xxs mt-4 text-center"></div>
              {errMessage && (
                <div className="text-red-500 text-xxs mb-2">{errMessage}</div>
              )}
            </div>
          )}
        </div>
      </div>
      <Form
        id="assignApprover"
        form={form}
        name="normal_login"
        className="login-form"
        onFinish={onSubmit}
        layout="vertical"
      ></Form>
    </Modal>
  );
}
